/**
 * Model Loader with Native Bridge Communication
 * Handles lazy loading of ML models from native iOS/Android
 */

import * as tf from "@tensorflow/tfjs";
import { loadingStateManager, ModelLoadingState } from "./loading-state";
import { secureRandom } from "../utils/secureRandom";

// Model identifiers
export const MODEL_KEYS = {
  MOVENET: "movenet",
  BLAZEPOSE_DETECTOR: "blazeposeDetector",
  BLAZEPOSE_LANDMARK: "blazeposeLandmark"
};

// IndexedDB keys for model storage
export const IDB_KEYS = {
  MOVENET: "indexeddb://movenet-default",
  BLAZEPOSE_DETECTOR: "indexeddb://blazepose-detector",
  BLAZEPOSE_LANDMARK: "indexeddb://blazepose-landmark"
};

// Last-resort sources, used only when the native bridge and our own server
// both fail. Same models pose-detection defaults to (MoveNet multipose
// lightning, BlazePose full), written as the model.json URLs tfjs builds for
// tfhub; tf.io.http keeps the ?tfjs-format=file query on every weight shard.
export const TFHUB_MODEL_URLS = {
  MOVENET: "https://tfhub.dev/google/tfjs-model/movenet/multipose/lightning/1/model.json?tfjs-format=file",
  BLAZEPOSE_DETECTOR: "https://tfhub.dev/mediapipe/tfjs-model/blazepose_3d/detector/1/model.json?tfjs-format=file",
  BLAZEPOSE_LANDMARK: "https://tfhub.dev/mediapipe/tfjs-model/blazepose_3d/landmark/full/2/model.json?tfjs-format=file"
};

/**
 * Request model from native side
 * @param {string} modelName - Name of the model to request
 * @returns {Promise<Object>} Model data in base64 format
 */
async function requestModelFromNative(modelName) {
  return new Promise((resolve, reject) => {
    // Create a unique callback ID
    const callbackId = `model_callback_${Date.now()}_${secureRandom()}`;
    
    // Set up the callback
    window[callbackId] = (modelData) => {
      // Clean up the callback
      delete window[callbackId];
      
      if (modelData && modelData.error) {
        reject(new Error(modelData.error));
      } else {
        resolve(modelData);
      }
    };
    
    // Request model from native
    // This assumes the native side has a handler for this message
    if (window.webkit?.messageHandlers?.requestModel) {
      // iOS WKWebView
      window.webkit.messageHandlers.requestModel.postMessage({
        modelName: modelName,
        callbackId: callbackId
      });
    } else {
      // Fallback: Check if models are already injected (old behavior)
      if (window.ML_MODELS_BASE64 && window.ML_MODELS_BASE64[modelName]) {
        resolve(window.ML_MODELS_BASE64[modelName]);
      } else {
        reject(new Error(`Native bridge not available for model: ${modelName}`));
      }
    }
    
    // Timeout after 30 seconds
    setTimeout(() => {
      if (window[callbackId]) {
        delete window[callbackId];
        reject(new Error(`Timeout waiting for model: ${modelName}`));
      }
    }, 30000);
  });
}

/**
 * Convert base64 to Blob
 */
function base64ToBlob(base64, mimeType = 'application/octet-stream') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/**
 * Create File objects from base64 model data
 */
function createModelFiles(modelData) {
  const files = [];
  
  // Add JSON file
  if (modelData.json) {
    const jsonBlob = base64ToBlob(modelData.json, 'application/json');
    files.push(new File([jsonBlob], 'model.json', { type: 'application/json' }));
  }
  
  // Add shard files with correct naming convention
  let shardIndex = 0;
  const shardKeys = Object.keys(modelData).filter(key => key.startsWith('shard')).sort();
  const totalShards = shardKeys.length;
  
  for (const shardKey of shardKeys) {
    const shardBlob = base64ToBlob(modelData[shardKey]);
    // Use the expected TensorFlow.js naming convention
    const fileName = `group1-shard${shardIndex + 1}of${totalShards}.bin`;
    files.push(new File([shardBlob], fileName, { type: 'application/octet-stream' }));
    shardIndex++;
  }
  
  return files;
}

/**
 * Resolve where a model should be loaded from. Nothing is turned into a
 * GraphModel here; pose-detection does that once, in createDetector.
 * Cached models resolve to their IndexedDB key. Otherwise the weights come
 * from the native bridge, or from the first fallback URL that loads, are
 * cached to IndexedDB, and are returned as an in-memory IOHandler.
 * @param {string} modelKey - Key from MODEL_KEYS
 * @param {string} idbKey - Key from IDB_KEYS
 * @param {string[]} fallbackUrls - model.json URLs to try in order when the
 *   native bridge fails (e.g. our server, then TFHUB_MODEL_URLS)
 * @returns {Promise<string|tf.io.IOHandler>} Value for modelUrl /
 *   detectorModelUrl / landmarkModelUrl
 */
export async function loadModelLazy(modelKey, idbKey, fallbackUrls = []) {
  loadingStateManager.setModelState(modelKey, ModelLoadingState.CHECKING_CACHE);

  const storedModels = await tf.io.listModels();
  if (storedModels[idbKey]) {
    console.log(`${modelKey} found in IndexedDB, using cached version`);
    loadingStateManager.setModelState(modelKey, ModelLoadingState.READY);
    return idbKey;
  }

  console.log(`${modelKey} not in IndexedDB, requesting from native...`);
  loadingStateManager.setModelState(modelKey, ModelLoadingState.REQUESTING_FROM_NATIVE);

  let artifacts;
  try {
    const modelData = await requestModelFromNative(modelKey);
    if (!modelData) {
      throw new Error(`No model data received for ${modelKey}`);
    }

    console.log(`Loading ${modelKey} from native bridge...`);
    loadingStateManager.setModelState(modelKey, ModelLoadingState.LOADING_MODEL);
    artifacts = await tf.io.browserFiles(createModelFiles(modelData)).load();
  } catch (error) {
    console.log(`Native bridge unavailable for ${modelKey} (${error.message})`);
    artifacts = await loadFromFallbacks(modelKey, fallbackUrls, error);
  }

  loadingStateManager.setModelState(modelKey, ModelLoadingState.SAVING_TO_CACHE);
  await cacheArtifacts(modelKey, idbKey, artifacts);

  loadingStateManager.setModelState(modelKey, ModelLoadingState.READY);
  return tf.io.fromMemory(artifacts);
}

/**
 * Try each fallback URL in order and return the first model that loads.
 * Throws the last error (or the native error if there are no fallbacks) once
 * every source has failed.
 */
async function loadFromFallbacks(modelKey, fallbackUrls, nativeError) {
  let lastError = nativeError;
  for (const url of fallbackUrls) {
    console.log(`Loading ${modelKey} from ${url}`);
    loadingStateManager.setModelState(modelKey, ModelLoadingState.LOADING_MODEL);
    try {
      return await tf.io.http(url).load();
    } catch (error) {
      console.log(`Could not load ${modelKey} from ${url} (${error.message})`);
      lastError = error;
    }
  }
  loadingStateManager.setModelState(modelKey, ModelLoadingState.ERROR);
  throw lastError;
}

/**
 * Save model artifacts to IndexedDB without building a GraphModel.
 * A failed write is logged and ignored: the model still loads this session,
 * it just isn't cached for the next one.
 */
async function cacheArtifacts(modelKey, idbKey, artifacts) {
  try {
    await tf.io.getSaveHandlers(idbKey)[0].save(artifacts);
    console.log(`${modelKey} saved to IndexedDB`);
  } catch (error) {
    console.warn(`Could not cache ${modelKey} in IndexedDB:`, error);
  }
}

/**
 * Check if all required models are in IndexedDB
 */
export async function areModelsInCache() {
  const storedModels = await tf.io.listModels();
  
  const result = {
    movenet: !!storedModels[IDB_KEYS.MOVENET],
    blazeposeDetector: !!storedModels[IDB_KEYS.BLAZEPOSE_DETECTOR],
    blazeposeLandmark: !!storedModels[IDB_KEYS.BLAZEPOSE_LANDMARK],
    allCached: !!storedModels[IDB_KEYS.MOVENET] && 
               !!storedModels[IDB_KEYS.BLAZEPOSE_DETECTOR] && 
               !!storedModels[IDB_KEYS.BLAZEPOSE_LANDMARK]
  };
  console.log("areModelsInCache result:", result);
  return result;
}

/**
 * Clear all models from IndexedDB (useful for debugging/testing)
 */
export async function clearModelCache() {
  try {
    await tf.io.removeModel(IDB_KEYS.MOVENET);
    await tf.io.removeModel(IDB_KEYS.BLAZEPOSE_DETECTOR);
    await tf.io.removeModel(IDB_KEYS.BLAZEPOSE_LANDMARK);
    console.log("All models cleared from IndexedDB");
  } catch (error) {
    console.error("Error clearing model cache:", error);
  }
}